/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * Protected Route Component
 *
 * - NEW FILE
 * - MISSION: To create a reusable component that acts as an authentication
 * guard for private routes like the dashboard.
 *
 * - FEATURES:
 * 1.  Authentication Check: Uses the `useUser` hook to get the current
 * user's authentication status and the loading state.
 * 2.  Graceful Loading State: While Firebase is checking the user's auth
 * status, it displays a full-screen spinner to prevent UI flashes or
 * premature redirects.
 * 3.  Conditional Rendering & Redirection:
 * - If authentication is still loading, it shows the spinner.
 * - If the user IS authenticated (`user` object exists), it renders the
 * child components (e.g., the Dashboard).
 * - If the user IS NOT authenticated (`user` is null), it uses the
 * `Maps` component from React Router to redirect them to the /login page.
 * 4.  Reusable Wrapper: This component is designed to wrap any route in
 * `App.jsx` that requires a user to be logged in.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useUser } from '@/hooks/useUser';
import { FaSpinner } from 'react-icons/fa';

const ProtectedRoute = () => {
  const { user, loading, isClaimsReady } = useUser();
  const location = useLocation();

  // 1. While the auth state and claims are being determined, show a loading spinner.
  if (loading || !isClaimsReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <FaSpinner className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  // 2. If loading is finished and there is a user, render the child route (e.g., Dashboard).
  //    The <Outlet /> component is a placeholder for the nested route element.
  if (user?.uid) {
    return <Outlet />;
  }

  // 3. If loading is finished and there is no user, redirect to the login page.
  return <Navigate to="/login" state={{ from: location }} replace />;
};

export default ProtectedRoute;
